/**
 * Conversa treinador↔atleta (texto simples). Serve para a conversa geral e para os comentários de uma atividade.
 * - `self`: o próprio atleta (usa /me/messages); sem `self`: a equipe (usa /athletes/{id}/messages).
 * - Abrir a conversa marca como lidos os avisos dela; a lista se atualiza sozinha a cada 4 s enquanto a aba está visível
 *   (e na hora em que a aba volta a ficar visível ou a janela ganha foco). Só reaplica o estado quando chega algo novo.
 * - O texto nunca é interpretado como HTML (React escapa), e só o autor apaga a própria mensagem.
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { api, ApiError } from "../api/client";
import type { ChatMessage, ChatThread } from "../api/types";
import { formatDateTime } from "../lib/format";
import { Button, ErrorState, Loading, useToast } from "./ui";
import { t } from "../i18n";

const MAX = 2000;
const POLL_MS = 4_000;

export function ConversationPanel({
  orgId,
  athleteId,
  activityId = null,
  self = false,
  empty,
}: {
  orgId: string;
  athleteId?: string;
  activityId?: string | null;
  self?: boolean;
  empty?: string;
}) {
  const toast = useToast();
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const lastCount = useRef(0);
  const lastSig = useRef("");
  const epoch = useRef(0); // descarta respostas de uma conversa que já não é a exibida
  const areaId = useId();

  const base = self ? `/orgs/${orgId}/me/messages` : `/orgs/${orgId}/athletes/${athleteId}/messages`;
  const query = activityId ? { activity_id: activityId } : undefined;

  const load = useCallback(
    async (markRead: boolean, forceRead = false) => {
      const mine = epoch.current;
      try {
        const tp = await api.get<ChatThread>(base, query);
        if (mine !== epoch.current) return;
        // sem novidade: não re-renderiza (evita piscar e perder a posição de rolagem a cada consulta)
        const sig = tp.messages.map((m) => `${m.id}:${m.deleted ? 1 : 0}`).join(",") + `|${tp.has_more}|${tp.can_post}`;
        const fresh = sig !== lastSig.current;
        lastSig.current = sig;
        if (fresh) setThread(tp);
        setError(null);
        if (markRead && (fresh || forceRead)) {
          // só marca como lidos os avisos até a mensagem mais nova que ESTA tela mostrou
          const newest = tp.messages.length ? tp.messages[tp.messages.length - 1].created_at : undefined;
          await api.post(`/orgs/${orgId}/messages/read`, {
            athlete_id: self ? null : athleteId,
            activity_id: activityId,
            until: newest ?? null,
          });
          window.dispatchEvent(new Event("eapace:notifications"));
        }
      } catch (e) {
        if (mine !== epoch.current) return;
        setError(e instanceof ApiError ? e : new ApiError(0, "unknown", String(e)));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, activityId, orgId, athleteId, self],
  );

  useEffect(() => {
    epoch.current += 1;
    setThread(null);
    setText("");
    setError(null);
    lastCount.current = 0;
    lastSig.current = "";
    if (!self && !athleteId) return;
    void load(true, true);
    const tick = () => {
      if (document.visibilityState === "visible") void load(true);
    };
    const timer = window.setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    window.addEventListener("online", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
      window.removeEventListener("online", tick);
      epoch.current += 1;
    };
  }, [load, self, athleteId]);

  useEffect(() => {
    const n = thread?.messages.length ?? 0;
    if (n > lastCount.current && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    lastCount.current = n;
  }, [thread]);

  async function send(e?: FormEvent) {
    e?.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api.post<ChatMessage>(base, { body, activity_id: activityId });
      setText("");
      await load(false, false);
    } catch (err) {
      toast((err as ApiError).message, "error");
    } finally {
      setSending(false);
    }
  }

  async function remove(m: ChatMessage) {
    if (!window.confirm(t("Apagar esta mensagem? O texto é descartado para todos."))) return;
    try {
      await api.delete(`/orgs/${orgId}/messages/${m.id}`);
      await load(false, false);
    } catch (err) {
      toast((err as ApiError).message, "error");
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void send();
  }

  if (error && !thread) return <ErrorState error={error} onRetry={() => void load(true)} />;
  if (!thread) return <Loading label={t("Carregando conversa…")} />;

  return (
    <div className="chat">
      {thread.has_more && <p className="muted chat-more">{t("Mostrando as 300 mensagens mais recentes.")}</p>}
      {thread.messages.length === 0 ? (
        <p className="muted chat-empty">{empty ?? t("Nenhuma mensagem ainda.")}</p>
      ) : (
        <ul className="chat-list" ref={listRef} aria-label={t("Mensagens")} tabIndex={0}>
          {thread.messages.map((m) => (
            <li key={m.id} className={`chat-msg ${m.mine ? "mine" : "theirs"} ${m.author_kind}`}>
              <div className="chat-meta">
                <strong>{m.mine ? t("Você") : (m.author_name ?? (m.author_kind === "staff" ? t("Equipe") : t("Atleta")))}</strong>
                <span className="chat-role">{m.author_kind === "staff" ? t("Equipe") : t("Atleta")}</span>
                <time dateTime={m.created_at}>{formatDateTime(m.created_at)}</time>
              </div>
              {m.deleted ? (
                <p className="chat-body chat-deleted">{t("Mensagem apagada.")}</p>
              ) : (
                <p className="chat-body">{m.body}</p>
              )}
              {m.mine && !m.deleted && (
                <button className="chat-del" onClick={() => void remove(m)} aria-label={t("Apagar mensagem")}>
                  {t("Apagar")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {thread.can_post && (
        <form className="chat-form" onSubmit={send}>
          <label htmlFor={areaId} className="chat-label">
            {self ? t("Escreva para a equipe") : t("Escreva para {athlete_name}", { athlete_name: thread.athlete_name })}
          </label>
          <textarea
            id={areaId}
            className="textarea"
            rows={3}
            maxLength={MAX}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            placeholder={activityId ? t("Comente esta atividade…") : t("Escreva uma mensagem…")}
          />
          <div className="chat-actions">
            <span className="muted chat-hint">
              {self
                ? t("Não escreva dados de saúde detalhados aqui: para isso existe o check-in de bem-estar. ")
                : t("Visível ao atleta e à equipe dele. ")}
              {t("Ctrl+Enter envia · {n}/{max}", { n: text.length, max: MAX })}
            </span>
            <Button type="submit" variant="primary" busy={sending} disabled={!text.trim()}>
              {t("Enviar")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
